import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { createGameState } from '@/engine/state/createGameState';
import {
  dispatch, takeCardAction, declineCardAction, nextRoundAction, standings, canDecline, cardsRemaining,
  isRoundOver, matchInfo,
} from '@/engine/actions';
import { decideAction, personalityFor, PERSONALITIES } from '@/engine/ai/heuristicBot';
import { PHASES, clampRounds } from '@/engine/constants';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, bindRoom, commitAction, setTableSize, setRounds,
  sweepMyRooms, recordWaiting, rematchState,
  shouldAutoPlay, personalityForSeat, seqOf, DISCONNECT_GRACE_MS,
} from '@/store/multiplayer';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';

// The single place engine `dispatch()` is called from, same convention as the
// space-base and thunderstone repos. Components never touch engine state
// directly; they commit intents and read getters.

const SAVE_KEY = 'no-thanks:saved-game';
const SETTINGS_KEY = 'no-thanks:settings';

// How long a bot "thinks" before acting. Not a fake difficulty knob - it
// exists so a chain of bot turns is legible as a sequence of decisions rather
// than the card silently teleporting round the table between your turns.
//
// Declines are much quicker than takes, and the gap is deliberate. A real
// game runs about 175 turns, of which the overwhelming majority are declines
// (43 to 7 in a mid-game sample) - so the decline pause is what actually sets
// the pace, and it's paid three-plus times between each of your own turns.
// A take is the moment worth watching: a card leaves the table and a pile of
// counters changes hands.
//
// Bug report (2026-08-27): "We should vary the amount of time the computers
// pause just for a little bit of drama. We can make them take a little
// longer, so it isn't quite so snappy." Both halves of that:
//
//  - LONGER. 380/750 was tuned down from 620/900 to cut dead waiting, and it
//    overshot - three bots declining in 1.1s reads as a machine resolving,
//    not as three people deciding.
//  - VARIED. A fixed pause is what makes it feel mechanical, and it is the
//    more important half. Every bot turn now lands somewhere in a window, so
//    a lap round the table has an uneven rhythm the way a real one does.
export const BOT_DECLINE_MS = 520;
export const BOT_TAKE_MS = 1000;

// How far either side of those a given turn may land, as a fraction. 0.3
// gives a decline 364-676ms and a take 700-1300ms - clearly uneven, never so
// slow that a lap of three bots outstays its welcome.
//
// The ceiling on this is not taste, it is arithmetic: past ~0.31 the two
// windows overlap and a fast take starts landing quicker than a slow decline,
// which erases the one distinction these pauses exist to draw. A test asserts
// the windows stay apart, and it caught 0.45 doing exactly that.
export const BOT_PAUSE_JITTER = 0.3;

/**
 * A pause for one bot turn: the base, spread across a window.
 *
 * `Math.random` is fine here and forbidden three directories away - this is
 * presentation, not the engine. Nothing about the game state depends on it, so
 * a seeded replay is unaffected by how long anyone appeared to think.
 */
export const botPause = (base, random = Math.random) => (
  Math.round(base * (1 + (random() * 2 - 1) * BOT_PAUSE_JITTER))
);

// The pending bot-turn timer, if any.
//
// Module-level rather than in state because it's a handle, not data - it must
// never be serialized into a save. Its job is to keep exactly ONE bot chain
// alive at a time. Without it, two chains can be armed at once (resumeGame
// and App's mounted hook can both fire; so can a human action landing while a
// bot turn is already queued), and then two timers each dispatch a decision
// that was computed against a now-stale state - the bots move twice, or the
// thinkingPlayerId flag is left set by one chain after the other cleared it,
// which locks the buttons permanently with no way back.
//
// A locked-up board was observed once during manual testing (thinkingPlayerId
// stuck on a bot with no timer pending), which is what prompted this.
let botTimer = null;

// Live listener teardown for the current room, and the auth watcher.
let unbindRoomFn = null;
let unwatchStatusFn = null;
// When each absent player was last seen. Not in state and not in the DB: it
// is a purely local observation ("I noticed you go") and every client makes
// it independently, which is exactly what the grace period wants.
const lastSeen = {};

const cancelBotTurn = () => {
  if (botTimer !== null) {
    window.clearTimeout(botTimer);
    botTimer = null;
  }
};

const loadJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or disabled localStorage must never break play.
  }
};

export default createStore({
  state: {
    game: null,
    // Set while a bot's turn is pending, so the UI can show whose it is and
    // disable the human's buttons without inspecting timers.
    thinkingPlayerId: null,
    settings: loadJson(SETTINGS_KEY) || { playerName: 'You', opponents: 3, rounds: 1 },
    hasSavedGame: Boolean(loadJson(SAVE_KEY)),

    // ---- sign-in and the shared approval gate
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,          // { status: 'pending'|'approved'|'denied', isAdmin }

    // ---- online play
    roomId: null,
    roomMeta: null,          // { hostUid, status, roomCode, playerSlots, ... }
    presence: {},            // { uid: true } for everyone currently connected
    roomError: null,
  },

  getters: {
    inPlay: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.PLAYING,
    isFinished: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.FINISHED,
    // The score sheet's cue: a round has ended, whether or not the match has.
    // Also what lifetime stats watch, since each round is recorded as a game.
    isRoundOver: (state) => isRoundOver(state.game),
    match: (state) => (state.game ? matchInfo(state.game) : null),
    activePlayer: (state) => (state.game
      ? state.game.players[state.game.meta.activePlayerIndex]
      : null),
    humanPlayer: (state) => state.game?.players.find((p) => !p.isBot) || null,
    isHumanTurn: (state, getters) => Boolean(
      getters.inPlay && getters.activePlayer && !getters.activePlayer.isBot && !state.thinkingPlayerId,
    ),
    canDecline: (state) => (state.game ? canDecline(state.game) : false),
    isOnline: (state) => Boolean(state.roomId),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,

    // Me, in both modes: online, player ids ARE Firebase uids; locally the
    // one non-bot seat. Same getter, same wording, in every sibling game.
    me: (state) => {
      if (!state.game) return null;
      if (state.roomId) return state.game.players.find((p) => p.id === state.user?.uid) || null;
      return state.game.players.find((p) => !p.isBot) || null;
    },
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    // Online, "my turn" means the active seat is mine AND I'm the one the
    // rules say should be writing. Offline it's the old local check.
    isMyOnlineTurn: (state) => {
      if (!state.roomId || !state.game) return false;
      const active = state.game.players[state.game.meta.activePlayerIndex];
      return Boolean(active && !active.isBot && active.id === state.user?.uid);
    },
    // Seats whose human has dropped out. Drives the "Steady is playing
    // Brian's hand" notice.
    absentPlayerIds: (state) => {
      if (!state.roomId || !state.game) return [];
      return state.game.players
        .filter((p) => !p.isBot && !state.presence[p.id])
        .map((p) => p.id);
    },
    cardsLeft: (state) => (state.game ? cardsRemaining(state.game) : 0),
    scores: (state) => (state.game ? standings(state.game) : null),
    // Opponents in seat order starting after the human, so the table reads
    // round-the-left the way the rulebook describes play passing.
    opponentsInTurnOrder: (state, getters) => {
      if (!state.game) return [];
      const human = getters.humanPlayer;
      const { players } = state.game;
      const start = human ? human.seat : 0;
      return players
        .map((_, i) => players[(start + 1 + i) % players.length])
        .filter((p) => p.id !== human?.id);
    },
  },

  mutations: {
    setGame(state, game) {
      state.game = game;
      state.hasSavedGame = Boolean(game);
      saveJson(SAVE_KEY, game);
    },
    setThinking(state, playerId) {
      state.thinkingPlayerId = playerId;
    },
    setSettings(state, settings) {
      state.settings = { ...state.settings, ...settings };
      saveJson(SETTINGS_KEY, state.settings);
    },
    clearGame(state) {
      state.game = null;
      state.thinkingPlayerId = null;
      state.hasSavedGame = false;
      saveJson(SAVE_KEY, null);
    },
    setAuthReady: (state, v) => { state.authReady = v; },
    setUser: (state, user) => { state.user = user; },
    setSiteUser: (state, su) => { state.siteUser = su; state.siteUserReady = true; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setRoom: (state, { id, meta }) => { state.roomId = id; state.roomMeta = meta ?? null; },
    setRoomMeta: (state, meta) => { state.roomMeta = meta; },
    setPresence: (state, p) => { state.presence = p || {}; },
    setRoomError: (state, e) => { state.roomError = e; },
    // Online state arrives from the listener, so it must NOT be written back
    // to localStorage - that save slot is for the local game only, and
    // stamping a remote game into it would resurrect it as a "saved game"
    // on the menu after everyone had left.
    setRemoteGame(state, game) { state.game = game; },
  },

  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the game has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({
        game: state.game,
        myId: getters.me?.id,
        online: Boolean(state.roomId),
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ game: state.game, roomId: state.roomId }),
        outcome,
      });
    },

    newGame({ commit, dispatch: dispatchAction, state }, {
      playerName, opponents, rounds, seed,
    } = {}) {
      const name = playerName || state.settings.playerName || 'You';
      const botCount = opponents || state.settings.opponents || 3;
      const roundCount = clampRounds(rounds ?? state.settings.rounds ?? 1);

      // The human always sits first, so "the player to your left" in the
      // rulebook is literally the next row down the screen.
      const players = [
        { id: 'you', name, isBot: false },
        ...Array.from({ length: botCount }, (_, i) => ({
          id: `bot${i}`,
          name: PERSONALITIES[personalityFor(i)].label,
          isBot: true,
        })),
      ];

      cancelBotTurn();
      commit('setSettings', { playerName: name, opponents: botCount, rounds: roundCount });
      commit('setGame', createGameState({ players, seed: seed ?? Date.now(), rounds: roundCount }));
      dispatchAction('runBotsIfNeeded');
    },

    resumeGame({ commit, dispatch: dispatchAction }) {
      const saved = loadJson(SAVE_KEY);
      if (!saved) return;
      cancelBotTurn();
      commit('setGame', saved);
      // A game saved mid-bot-turn resumes with that bot still to move.
      dispatchAction('runBotsIfNeeded');
    },

    take({ commit, dispatch: dispatchAction, state, getters }) {
      if (state.roomId) { dispatchAction('playOnline', takeCardAction().type); return; }
      if (!getters.isHumanTurn) return;
      commit('setGame', dispatch(state.game, takeCardAction()));
      dispatchAction('runBotsIfNeeded');
    },

    decline({ commit, dispatch: dispatchAction, state, getters }) {
      if (state.roomId) {
        if (!canDecline(state.game)) return;
        dispatchAction('playOnline', declineCardAction().type);
        return;
      }
      if (!getters.isHumanTurn || !getters.canDecline) return;
      commit('setGame', dispatch(state.game, declineCardAction()));
      dispatchAction('runBotsIfNeeded');
    },

    /**
     * Deals the next round of a match. Not anybody's turn, so online any
     * seated player may press it; the seq check in commitAction means two
     * people pressing at once still deal exactly one round.
     */
    async nextRound({ commit, dispatch: dispatchAction, state }) {
      if (!state.game || state.game.meta.phase !== PHASES.ROUND_OVER) return;
      if (!state.roomId) {
        cancelBotTurn();
        commit('setGame', dispatch(state.game, nextRoundAction()));
        dispatchAction('runBotsIfNeeded');
        return;
      }
      try {
        await commitAction({
          gameId: state.roomId,
          expectedSeq: seqOf(state.game),
          apply: (current) => dispatch(current, nextRoundAction()),
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    /**
     * Online only: a new game in the same room, same table (rematchState).
     * Like Next round, any seated player may press it and the seq check
     * deals exactly one. The seed is drawn here, outside the engine.
     */
    async rematch({ commit, state }) {
      if (!state.roomId || state.game?.meta.phase !== PHASES.FINISHED) return;
      if (!state.game.players.some((p) => p.id === state.user?.uid)) return;
      trackEvent('rematch');
      const seed = Math.floor(Math.random() * 2 ** 31);
      try {
        const result = await commitAction({
          gameId: state.roomId,
          expectedSeq: seqOf(state.game),
          apply: (current) => rematchState(current, seed),
        });
        if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    /**
     * Plays out every consecutive bot turn, one at a time, with a pause
     * between so the table is watchable.
     *
     * Recursive-with-a-timeout rather than a loop: a bot that takes a card
     * keeps the turn (rulebook p2, Game Play 3), so "the bots' turn" is not a
     * fixed number of steps - it can be one decline or a dozen takes, and it
     * ends only when the seat comes back to a human or the game does.
     */
    runBotsIfNeeded({ commit, dispatch: dispatchAction, state, getters }) {
      // Another chain already has a turn queued; it will recurse when it
      // lands. Arming a second one here is what causes double moves.
      if (botTimer !== null) return;

      if (!getters.inPlay) {
        commit('setThinking', null);
        return;
      }
      const active = getters.activePlayer;
      if (!active?.isBot) {
        commit('setThinking', null);
        return;
      }

      commit('setThinking', active.id);
      const actingId = active.id;

      // Peeked at only to choose how long to pause - a take is worth watching
      // for longer than a decline. The decision actually PLAYED is recomputed
      // when the timer fires, from whatever the state is then.
      const preview = decideAction(state.game, personalityFor(active.seat - 1));
      const pause = botPause(preview.type === takeCardAction().type ? BOT_TAKE_MS : BOT_DECLINE_MS);

      botTimer = window.setTimeout(() => {
        botTimer = null;
        // The game can have been abandoned or replaced while this was pending.
        if (!state.game || state.game.meta.phase !== PHASES.PLAYING) {
          commit('setThinking', null);
          return;
        }

        // Whoever is active NOW is who gets to move. Dispatching the decision
        // computed when the timer was armed is a real bug, not a theoretical
        // one: if the active seat changed in between, the move lands on the
        // wrong player. It was caught in the browser as a log line reading
        // "Matthew declines" on a turn the human never took - a bot's queued
        // decline had been applied to the human's seat.
        const current = state.game.players[state.game.meta.activePlayerIndex];
        if (!current?.isBot || current.id !== actingId) {
          commit('setThinking', null);
          dispatchAction('runBotsIfNeeded');
          return;
        }

        const decision = decideAction(state.game, personalityFor(current.seat - 1));
        commit('setGame', dispatch(state.game, { type: decision.type }));
        commit('setThinking', null);
        dispatchAction('runBotsIfNeeded');
      }, pause);
    },

    // ---------------------------------------------------------------- auth

    initAuth({ commit, dispatch: dispatchAction }) {
      if (!firebaseConfigured || !auth) {
        // No Firebase configured at all: local play still works completely,
        // and the app just never offers online games. Same "degrade, don't
        // break" arrangement as the sibling repos.
        commit('setAuthReady', true);
        return;
      }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player: treat it
        // as signed out, and never write it a siteUsers record.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? {
          uid: user.uid, displayName: user.displayName, email: user.email,
        } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
          // A name from Google is a better default than "You".
          if (user.displayName) dispatchAction('rememberName', user.displayName);
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    rememberName({ commit, state }, name) {
      if (!name || state.settings.playerName === name) return;
      commit('setSettings', { playerName: name });
    },

    async signIn({ commit }) {
      if (!auth) throw new Error('Sign-in needs Firebase configured.');
      try {
        markInteractiveSignIn();
        await signInWithPopup(auth, googleProvider);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async signOutOfSite({ commit, dispatch: dispatchAction }) {
      await dispatchAction('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ------------------------------------------------------------ rooms

    /**
     * Housekeeping: bin my own abandoned rooms. Fire-and-forget, called when
     * the online lobby opens. Never awaited by anything the player is waiting
     * on - a slow or failed sweep must not delay starting a game.
     */
    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch: dispatchAction }, { tableSize, async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({
          user: state.user,
          tableSize: tableSize ?? (state.settings.opponents || 3) + 1,
          async: asyncRoom,
        });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async joinRoomByCode({ commit, state, dispatch: dispatchAction }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    enterRoom({ commit, state, dispatch: dispatchAction }, gameId) {
      cancelBotTurn();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setRemoteGame', null);

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => {
          commit('setRoomMeta', meta);
          // The room was deleted out from under us.
          if (meta === null) dispatchAction('leaveRoom');
        },
        onState: (game) => {
          commit('setRemoteGame', game);
          dispatchAction('driveOnlineTurn');
        },
        onPresence: (presence) => {
          const now = Date.now();
          // Note the moment anyone vanishes, so the grace period can run.
          Object.keys(lastSeen).forEach((uid) => { if (presence[uid]) delete lastSeen[uid]; });
          (state.game?.players || []).forEach((p) => {
            if (!p.isBot && !presence[p.id] && !lastSeen[p.id]) lastSeen[p.id] = now;
          });
          commit('setPresence', presence);
          dispatchAction('driveOnlineTurn');
        },
      });
    },

    async leaveRoom({ commit, state }) {
      cancelBotTurn();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      commit('setRemoteGame', null);
      commit('setThinking', null);
    },

    async changeTableSize({ commit, state }, tableSize) {
      try {
        await setTableSize({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, tableSize,
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async changeRounds({ commit, state }, rounds) {
      try {
        await setRounds({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, rounds,
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async dealRoom({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // ------------------------------------------------- the online turn loop

    /**
     * Called on every state and presence change. Works out whether THIS
     * browser is the one that owes the table a move, and if so plays it.
     *
     * A connected human plays their own turn by tapping a button, so this
     * only ever fires for a bot seat or for somebody who has dropped out -
     * and even then only on the one client that shouldActFor picks.
     */
    driveOnlineTurn({ state, dispatch: dispatchAction, commit }) {
      if (!state.roomId || !state.game || botTimer !== null) return;
      if (state.game.meta.phase !== PHASES.PLAYING) { commit('setThinking', null); return; }

      const mine = shouldAutoPlay({
        gameState: state.game,
        presence: state.presence,
        myUid: state.user?.uid,
        disconnectedSince: lastSeen,
        asyncRoom: Boolean(state.roomMeta?.async),
      });
      if (!mine) {
        // Somebody else owes the move, but an absent player's grace period
        // may still be counting down - re-check when it expires, or nothing
        // would ever wake this up (presence has already stopped changing).
        const active = state.game.players[state.game.meta.activePlayerIndex];
        if (active && !active.isBot && !state.presence[active.id] && lastSeen[active.id]) {
          const waited = Date.now() - lastSeen[active.id];
          if (waited < DISCONNECT_GRACE_MS) {
            botTimer = window.setTimeout(() => {
              botTimer = null;
              dispatchAction('driveOnlineTurn');
            }, DISCONNECT_GRACE_MS - waited + 100);
          }
        }
        commit('setThinking', null);
        return;
      }

      const active = state.game.players[state.game.meta.activePlayerIndex];
      commit('setThinking', active.id);
      const preview = decideAction(state.game, personalityForSeat(active));
      const pause = botPause(preview.type === takeCardAction().type ? BOT_TAKE_MS : BOT_DECLINE_MS);

      botTimer = window.setTimeout(async () => {
        botTimer = null;
        commit('setThinking', null);
        if (!state.roomId || !state.game || state.game.meta.phase !== PHASES.PLAYING) return;
        const expectedSeq = seqOf(state.game);
        try {
          const result = await commitAction({
            gameId: state.roomId,
            expectedSeq,
            // Recomputed against whatever the transaction actually finds, for
            // the same reason the local loop does it: the seat may have moved
            // on between arming this and it firing.
            apply: (current) => {
              const seat = current.players[current.meta.activePlayerIndex];
              if (!seat) return null;
              const decision = decideAction(current, personalityForSeat(seat));
              return dispatch(current, { type: decision.type });
            },
          });
          if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
        } catch (error) {
          commit('setRoomError', error.message);
        }
      }, pause);
    },

    /**
     * A human's own move in an online game. Same engine action as local play;
     * only the write target differs.
     */
    async playOnline({ state, commit, getters }, type) {
      if (!getters.isMyOnlineTurn) return;
      const expectedSeq = seqOf(state.game);
      try {
        const { committed, state: committedState } = await commitAction({
          gameId: state.roomId,
          expectedSeq,
          apply: (current) => {
            const seat = current.players[current.meta.activePlayerIndex];
            // Guard inside the transaction too: by the time this runs, the
            // turn may genuinely no longer be mine.
            if (!seat || seat.id !== state.user?.uid) return null;
            return dispatch(current, { type });
          },
        });
        if (committed) recordWaiting({ gameId: state.roomId, gameState: committedState, meta: state.roomMeta });
        if (!committed) commit('setRoomError', null); // someone beat us to it; the listener has the truth
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    abandonGame({ commit }) {
      cancelBotTurn();
      commit('clearGame');
    },
  },
});
